import type { ExpenseFrequency, RecurringExpense, RecurringExpenseCategory } from '@/types'
import { FREQUENCY_OCCURRENCES_PER_YEAR } from '@/types'

/**
 * Pure functions for recurring-expense math.
 *
 * Two views matter for the user:
 *   1. **Average monthly burn** — what does this expense cost over a steady-
 *      state year? Useful for long-term planning and the budget headline.
 *   2. **This month's actual** — does anything hit this calendar month?
 *      Critical for survival-floor accuracy in spiky months (March property
 *      tax, September auto insurance, etc.).
 */

/**
 * The amount this expense contributes to a steady-state monthly average.
 * Examples:
 *   - $100/mo subscription → $100
 *   - $1,200/yr property tax → $100
 *   - $800 semi-annual auto insurance → $133.33
 */
export function monthlyEquivalent(expense: RecurringExpense): number {
  if (!expense.isActive) return 0
  const occurrences = FREQUENCY_OCCURRENCES_PER_YEAR[expense.frequency]
  return (expense.amount * occurrences) / 12
}

/**
 * Whether this expense is billed in the given calendar month (1-12).
 *
 *   - Monthly expenses always return true.
 *   - Non-monthly without billingMonths set → falls back to "January only" so
 *     the math doesn't double-count. Encourages users to fill billingMonths.
 */
export function isExpenseBilledInMonth(expense: RecurringExpense, monthOfYear: number): boolean {
  if (!expense.isActive) return false
  if (expense.frequency === 'monthly') return true
  if (!expense.billingMonths || expense.billingMonths.length === 0) {
    return monthOfYear === 1
  }
  return expense.billingMonths.includes(monthOfYear)
}

/**
 * Total amount actually due in a given calendar month, summing every
 * expense that bills that month at its full per-occurrence amount.
 */
export function actualMonthExpenses(
  expenses: RecurringExpense[],
  monthOfYear: number,
  options: { essentialOnly?: boolean } = {}
): number {
  return expenses
    .filter((e) => (options.essentialOnly ? e.isEssential : true))
    .filter((e) => isExpenseBilledInMonth(e, monthOfYear))
    .reduce((sum, e) => sum + e.amount, 0)
}

/**
 * Steady-state monthly average across all expenses (sum of monthlyEquivalents).
 * Useful as the "expected month" budget number when no specific spike is hitting.
 */
export function averageMonthlyExpenses(
  expenses: RecurringExpense[],
  options: { essentialOnly?: boolean } = {}
): number {
  return expenses
    .filter((e) => (options.essentialOnly ? e.isEssential : true))
    .reduce((sum, e) => sum + monthlyEquivalent(e), 0)
}

/**
 * Categorised breakdown of monthly-equivalent spend, sorted by amount desc.
 * Powers the "where is my money going?" view.
 */
export function expenseBreakdownByCategory(
  expenses: RecurringExpense[]
): Array<{ category: RecurringExpenseCategory; monthly: number; count: number }> {
  const map = new Map<RecurringExpenseCategory, { monthly: number; count: number }>()
  for (const e of expenses) {
    if (!e.isActive) continue
    const monthly = monthlyEquivalent(e)
    const existing = map.get(e.category)
    if (existing) {
      existing.monthly += monthly
      existing.count += 1
    } else {
      map.set(e.category, { monthly, count: 1 })
    }
  }
  return Array.from(map.entries())
    .map(([category, v]) => ({ category, ...v }))
    .sort((a, b) => b.monthly - a.monthly)
}

/**
 * Resolves the month string ("YYYY-MM") to a 1-12 month-of-year number for
 * use with isExpenseBilledInMonth. Returns NaN for malformed input.
 */
export function monthOfYearFromString(monthStr: string): number {
  const parts = monthStr.split('-')
  if (parts.length !== 2) return NaN
  const monthPart = parts[1]
  if (monthPart === undefined) return NaN
  const m = parseInt(monthPart, 10)
  if (Number.isNaN(m) || m < 1 || m > 12) return NaN
  return m
}

/**
 * Convenience: which categories exist in a frequency-mismatched state? E.g.
 * a semi-annual expense without billingMonths set will silently default to
 * January-only — the UI can flag this so the user supplies the months.
 */
export function expensesMissingBillingMonths(expenses: RecurringExpense[]): RecurringExpense[] {
  return expenses.filter(
    (e) =>
      e.isActive && e.frequency !== 'monthly' && (!e.billingMonths || e.billingMonths.length === 0)
  )
}

/**
 * Validate that an expense's billingMonths matches its frequency cadence.
 * Returns null if valid, or an error message if not. Used by the editor.
 */
export function validateBillingMonths(
  frequency: ExpenseFrequency,
  billingMonths: number[] | undefined
): string | null {
  if (frequency === 'monthly') return null
  const months = billingMonths ?? []
  const expectedCount = FREQUENCY_OCCURRENCES_PER_YEAR[frequency]
  if (months.length === 0) {
    return `Pick at least one billing month for a ${frequency.replace('_', '-')} expense`
  }
  if (months.length !== expectedCount) {
    return `${frequency.replace('_', '-')} expects ${expectedCount} billing month${expectedCount > 1 ? 's' : ''}, got ${months.length}`
  }
  for (const m of months) {
    if (m < 1 || m > 12 || !Number.isInteger(m)) {
      return `Invalid month number: ${m}`
    }
  }
  if (new Set(months).size !== months.length) {
    return 'Duplicate billing months'
  }
  return null
}
